// Site & Delivery — Batch 1 rework (Feb 2026)
//
// Renders THREE side-by-side panels in a 3×1 grid:
//   1. Delivery & Cost        (method, band, override, carrier, delivery window)
//   2. Site & Access          (contact, access, parking, on-site facts, weight → vehicle)
//   3. Customer Site Notes    (free-text notes — what the customer told us about the site)
//
// The Quote Notes Summary block (customer-facing PDF preview) previously
// bolted onto the bottom of this panel has been extracted into a dedicated
// `QuoteNotesSummaryPanel` component so QuoteBuilder can pair it 50/50 with
// StaffNotesChat at the bottom of the page.
//
// Data model on `quote.Delivery` is unchanged — the same fields keep the same
// keys, only their visual grouping shifts.

import { useEffect, useMemo, useState } from "react";
import { Truck, MapPin, StickyNote, Weight, UserPlus, X } from "lucide-react";
import { onsiteAllAnswered } from "../_quoteHelpers";
import CarriageButton from "./CarriageControls";
import BuildTypeControls from "./BuildTypeControls";
import OnSiteFacts from "./OnSiteFacts";
import QuoteAddressPicker from "./QuoteAddressPicker";
import { T2Panel, T2Label } from "../_t2";
import { suggestVehicle, WEIGHT_BANDS } from "./_buildTypeContent";

export default function DeliveryPanel({ quote, delivery, onChange, subtotal, billing, addrBook, customerFacingNotes, onNotesChange, onBuildChange, onProjectChange, onPrefill, children }) {
  const vehicle  = useMemo(() => suggestVehicle(delivery.estimatedWeightKg), [delivery.estimatedWeightKg]);
  const patch    = (p) => onChange({ ...delivery, ...p });

  // Customer defaults for the site contact (name + phone).
  const cust = quote?.Customer || {};
  const custName = [cust.firstName, cust.lastName].filter(Boolean).join(" ").trim();
  const custPhone = cust.phone || "";
  const hasCustomer = !!(custName || custPhone);

  // Prefill the PRIMARY site contact from the customer when empty — non-destructive
  // (never overwrites what staff typed; runs only when the customer changes).
  useEffect(() => {
    if (!hasCustomer) return;
    const p = {};
    if (!delivery.siteContactName && custName) p.siteContactName = custName;
    if (!delivery.siteContactPhone && custPhone) p.siteContactPhone = custPhone;
    if (Object.keys(p).length) (onPrefill || onChange)({ ...delivery, ...p });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cust.personId, cust.email]);

  // Additional site staff (extra on-site contacts).
  const contacts = delivery.additionalContacts || [];
  const setContact = (i, p) => patch({ additionalContacts: contacts.map((c, idx) => (idx === i ? { ...c, ...p } : c)) });
  const addContact = () => patch({ additionalContacts: [...contacts, { name: "", phone: "" }] });
  const rmContact = (i) => patch({ additionalContacts: contacts.filter((_, idx) => idx !== i) });

  // One-time merge: the old "Notes to customer" (CustomerFacingNotes) and this
  // "Customer site notes" (delivery.notes) are now a single box. Fold any legacy
  // CustomerFacingNotes text in once per quote load (onNotesChange mirrors both).
  useEffect(() => {
    const cf = (customerFacingNotes || "").trim();
    if (!cf || !onNotesChange) return;
    const dn = (delivery.notes || "").trim();
    if (dn.includes(cf)) return;
    onNotesChange(dn ? `${dn}\n\n${cf}` : cf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote?._id]);

  return (
    <div
      className="t2-delivery-3col"
      style={{ display: "grid", gap: 16 }}
      data-testid="delivery-3col-grid"
    >

      {/* ── Box 2: Site & Access (Build type at the top) ───────────── */}
      <T2Panel icon={MapPin} title="Site & access" testid="panel-site-access">
        <div style={{ marginBottom: 12 }} data-testid="site-project-block">
          <T2Label>Project name / postcode</T2Label>
          <input className="t2-input" value={quote?.ProjectRef || ""} maxLength={120}
            onChange={(e) => onProjectChange?.(e.target.value)}
            placeholder="e.g. Smith loft conversion · BS7 8NN" data-testid="quote-project-ref" />
        </div>
        <div style={{ marginBottom: 12 }} data-testid="site-build-block">
          <T2Label>Build type</T2Label>
          <BuildTypeControls value={quote?.BuildType || ""} onChange={onBuildChange} testidPrefix="urgency" />
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
          <T2Label>Site contact</T2Label>
          {hasCustomer && (
            <button type="button" onClick={() => patch({ siteContactName: custName, siteContactPhone: custPhone })}
              className="t2-mono" data-testid="site-contact-use-customer"
              style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--t2-teal-ink)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
              title={`Use customer: ${custName}${custPhone ? ` · ${custPhone}` : ""}`}>
              Use customer
            </button>
          )}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <input className="t2-input" value={delivery.siteContactName || ""}
            onChange={(e) => patch({ siteContactName: e.target.value })}
            placeholder="Name" data-testid="delivery-site-contact-name" />
          <input className="t2-input t2-mono" value={delivery.siteContactPhone || ""}
            onChange={(e) => patch({ siteContactPhone: e.target.value })}
            placeholder="07…" data-testid="delivery-site-contact-phone" />
        </div>

        {/* Additional site staff — repeatable name+mobile blocks */}
        {contacts.map((c, i) => (
          <div key={i} style={{ marginTop: 8 }} data-testid={`site-extra-contact-${i}`}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
              <T2Label>Also on site #{i + 2}</T2Label>
              <div style={{ display: "flex", gap: 8 }}>
                {hasCustomer && (
                  <button type="button" onClick={() => setContact(i, { name: custName, phone: custPhone })}
                    className="t2-mono" data-testid={`site-extra-contact-use-customer-${i}`}
                    style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--t2-teal-ink)", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                    Use customer
                  </button>
                )}
                <button type="button" onClick={() => rmContact(i)} data-testid={`site-extra-contact-remove-${i}`}
                  title="Remove this contact"
                  style={{ display: "inline-flex", alignItems: "center", color: "var(--t2-grey3)", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <input className="t2-input" value={c.name || ""}
                onChange={(e) => setContact(i, { name: e.target.value })}
                placeholder="Name" data-testid={`site-extra-contact-name-${i}`} />
              <input className="t2-input t2-mono" value={c.phone || ""}
                onChange={(e) => setContact(i, { phone: e.target.value })}
                placeholder="07…" data-testid={`site-extra-contact-phone-${i}`} />
            </div>
          </div>
        ))}
        <button type="button" onClick={addContact} data-testid="site-add-staff"
          className="t2-mono"
          style={{ marginTop: 8, display: "inline-flex", alignItems: "center", gap: 4, fontSize: 9.5, textTransform: "uppercase", letterSpacing: ".05em", color: "var(--t2-teal-ink)", background: "rgba(47,200,203,0.08)", border: "1px solid rgba(47,200,203,0.25)", borderRadius: 5, padding: "4px 8px", cursor: "pointer" }}>
          <UserPlus className="w-3 h-3" /> Add site staff
        </button>

        <div style={{ marginTop: 8 }}>
          <T2Label>Access notes</T2Label>
          <input className="t2-input" value={delivery.access || ""}
            onChange={(e) => patch({ access: e.target.value })}
            placeholder="Kerbside only / yard access / narrow lane…"
            data-testid="delivery-access" />
        </div>
        <div style={{ marginTop: 8 }}>
          <T2Label>Parking</T2Label>
          <input className="t2-input" value={delivery.parking || ""}
            onChange={(e) => patch({ parking: e.target.value })}
            placeholder="Yellow lines 8–6 / street parking / none"
            data-testid="delivery-parking" />
        </div>

        <div style={{ marginTop: 10 }}>
          <T2Label>Est. weight + vehicle</T2Label>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input type="number" min="0" step="10" className="t2-input t2-mono"
              value={delivery.estimatedWeightKg ?? ""}
              onChange={(e) => patch({
                estimatedWeightKg: Number(e.target.value) || 0,
                vehicleSuggested: suggestVehicle(Number(e.target.value) || 0)?.vehicle || "",
              })}
              placeholder="kg"
              style={{ flex: 1 }}
              data-testid="delivery-weight" />
            <VehicleGuideButton />
          </div>
          {vehicle && (
            <div
              style={{
                marginTop: 6, padding: "6px 8px", borderRadius: 6,
                background: vehicle.forklift_needed ? "rgba(232, 165, 71, 0.10)" : "rgba(74, 191, 118, 0.10)",
                border: `1px solid ${vehicle.forklift_needed ? "rgba(232, 165, 71, 0.35)" : "rgba(74, 191, 118, 0.35)"}`,
                fontSize: 10.5,
              }}
              data-testid="delivery-vehicle-suggestion"
            >
              <div style={{ color: vehicle.forklift_needed ? "var(--t2-warn-ink)" : "var(--t2-ok-ink)", fontFamily: "var(--t2-mono)", textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 3 }}>
                {vehicle.weight_label}
              </div>
              <div style={{ color: "var(--t2-grey1)", fontWeight: 600 }}>{vehicle.vehicle}</div>
              <div style={{ color: "var(--t2-grey3)", marginTop: 2 }}>{vehicle.notes}</div>
            </div>
          )}
        </div>
      </T2Panel>

      {/* ── Box 3: On-site facts + Customer Site Notes (merged) ─────── */}
      <T2Panel icon={StickyNote} title="Customer site notes" testid="panel-customer-site-notes">
        <T2Label>On-site facts</T2Label>
        <div
          className={!delivery.onSiteChecked && !onsiteAllAnswered(delivery) ? "t2-attn-box" : undefined}
          style={{
            display: "flex", flexDirection: "column", gap: 8,
            padding: 10, borderRadius: 6, marginBottom: 12,
            background: "rgba(47, 200, 203, 0.04)",
            border: "1px solid rgba(47, 200, 203, 0.20)",
          }}
          data-testid="delivery-onsite-group"
        >
          <OnSiteFacts delivery={delivery} onPatch={patch} />
          <div style={{ borderTop: "1px solid rgba(47, 200, 203, 0.18)", marginTop: 2, paddingTop: 8 }}>
            <TickRow label="✓ Asked the customer about all of the above on the call"
              checked={!!delivery.onSiteChecked}
              onChange={(v) => patch({ onSiteChecked: v })}
              testid="delivery-onsite-checked" />
          </div>
        </div>

        <T2Label>Notes — site &amp; to customer</T2Label>
        <textarea className="t2-input" rows={5}
          value={delivery.notes || ""}
          onChange={(e) => (onNotesChange ? onNotesChange(e.target.value) : patch({ notes: e.target.value }))}
          placeholder="What the customer told us about their site + anything the customer should see on the quote (lead time, terms, offload requirements, prior issues…)"
          style={{ resize: "vertical", minHeight: 110 }}
          data-testid="delivery-notes" />
        <p style={{ fontSize: 10.5, color: "var(--t2-grey3)", marginTop: 6, fontFamily: "var(--t2-mono)", lineHeight: 1.5 }}>
          Appears in the customer-facing summary &amp; PDF; kept as the audit trail if delivery goes wrong.
        </p>
      </T2Panel>
      {children}
    </div>
  );
}


// ─── People on Site (bool + counter) ───────────────────────────────────
// ─── Tick row ──────────────────────────────────────────────────────────
function TickRow({ label, checked, onChange, testid }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
      <input type="checkbox" checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid} />
      <span style={{ fontSize: 11.5, color: "var(--t2-grey1)" }}>{label}</span>
    </label>
  );
}


// ─── Vehicle guide popover trigger ─────────────────────────────────────
function VehicleGuideButton() {
  const [show, setShow] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setShow(true)}
        title="Vehicle & weight guide"
        style={{
          padding: "3px 6px", background: "rgba(47, 200, 203, 0.10)",
          color: "var(--t2-teal-ink)", border: "1px solid rgba(47, 200, 203, 0.30)",
          borderRadius: 4, cursor: "pointer", fontSize: 9.5, height: 26,
          fontFamily: "var(--t2-mono)", textTransform: "uppercase", letterSpacing: ".05em",
          display: "inline-flex", alignItems: "center", gap: 3,
        }}
        data-testid="vehicle-guide-btn"
      >
        <Weight className="w-3 h-3" /> Guide
      </button>
      {show && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 60,
          background: "rgba(0, 0, 0, 0.55)",
          display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "8vh 20px",
        }} onClick={() => setShow(false)} data-testid="vehicle-guide-popover">
          <div onClick={(e) => e.stopPropagation()} style={{
            width: "min(640px, 100%)", background: "var(--t2-ink)",
            border: "1px solid rgba(47, 200, 203, 0.40)", borderRadius: 10, padding: 20,
            boxShadow: "0 20px 60px rgba(0,0,0,0.55)",
          }}>
            <h3 style={{ margin: 0, marginBottom: 12, fontSize: 15, color: "var(--t2-grey1)", fontWeight: 700 }}>
              Vehicle &amp; Weight Guide
            </h3>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: "22%" }}>Order weight</th>
                  <th style={{ ...thStyle, width: "28%" }}>Typical vehicle</th>
                  <th style={thStyle}>Notes</th>
                </tr>
              </thead>
              <tbody>
                {WEIGHT_BANDS.map((b) => (
                  <tr key={b.weight_label}>
                    <td style={{ ...tdStyle, color: "var(--t2-grey1)", fontWeight: 700 }}>{b.weight_label}</td>
                    <td style={tdStyle}>{b.vehicle}</td>
                    <td style={tdStyle}>{b.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

const thStyle = { padding: "6px 8px", textAlign: "left", color: "var(--t2-grey3)", fontFamily: "var(--t2-mono)", fontSize: 9.5, textTransform: "uppercase", letterSpacing: ".05em", borderBottom: "1px solid rgba(255,255,255,0.08)" };
const tdStyle = { padding: "8px", color: "var(--t2-grey2)", borderBottom: "1px solid rgba(255,255,255,0.04)" };
